import { Lock } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { useTranslations } from "@/hooks/use-translations"
import type { Mail } from "@/lib/data"
import { threadLabel } from "@/lib/whatsapp-threads"

interface WhatsAppLineSwitchProps {
  mail: Mail
  /** Id del hilo abierto ahora mismo. */
  selectedId: string
  onSelect: (conversationId: string) => void
}

/**
 * Cambiar de linea sin salir del cliente.
 *
 * Un contacto que escribe a dos numeros tiene dos conversaciones, y hasta ahora
 * eso se veia como dos tarjetas iguales en la bandeja. Aqui son una sola
 * tarjeta y este conmutador elige cual se lee. Con un solo hilo no se pinta:
 * un conmutador de una opcion no es un conmutador.
 */
export function WhatsAppLineSwitch({ mail, selectedId, onSelect }: WhatsAppLineSwitchProps) {
  const { t } = useTranslations()
  const hilos = mail.whatsappThreads || []

  if (mail.platform !== "whatsapp" || hilos.length <= 1) return null

  return (
    <div
      className="flex items-center gap-2 overflow-x-auto border-b px-3 py-2"
      data-testid="whatsapp-line-switch"
    >
      <span className="shrink-0 text-xs text-muted-foreground">
        {t("conversations.line_switch") || "Línea"}
      </span>

      {hilos.map((hilo) => {
        const activo = hilo.id === selectedId
        const etiqueta = threadLabel(hilo, hilos, t)

        return (
          <Button
            key={hilo.id}
            variant="ghost"
            size="xs"
            aria-pressed={activo}
            className={cn(
              "shrink-0 gap-1",
              activo && "bg-muted",
              // Archivado: se puede leer, pero se ve que no esta vivo.
              hilo.status === "archived" && !activo && "opacity-60",
            )}
            onClick={() => onSelect(hilo.id)}
          >
            {hilo.line?.isInternal && <Lock className="h-3 w-3" />}
            <span className="whitespace-nowrap">{etiqueta}</span>
            {hilo.unreadCount > 0 && (
              <Badge variant="secondary" className="h-4 px-1 text-[10px]">
                {hilo.unreadCount}
              </Badge>
            )}
          </Button>
        )
      })}
    </div>
  )
}
